const post = {
  id: "building-career-in-tech",
  title: "Why I'm Building a Career in Tech Without a Traditional Background",
  date: "2026-09-07",
  excerpt: "I do not see my path into tech as being behind. I see it as an intentional pivot toward work that gives me more freedom, creativity, and room to build.",
  content: `
I do not come from a traditional computer science background.

For a while, that felt like something I had to explain carefully. As if the first question was always going to be: why are you here now?

But the more I build, the less defensive I feel about it.

I am not behind. I am pivoting intentionally.

## Why Tech

Tech attracts me because it combines several things I care about: creativity, logic, independence, and the ability to build something useful from almost nothing.

There is a kind of freedom in that.

With software, an idea can become a working product. A small tool can solve a real problem. A personal project can turn into something other people use. The distance between learning, creating, testing, and improving feels unusually short.

That matters to me.

I like work where effort compounds. If I learn a new concept, I can use it again. If I build one app, the next one becomes clearer. If I understand a pattern, it starts showing up everywhere.

Tech also has business potential in a way that feels practical and motivating. A good product can scale beyond one location, one schedule, or one person's direct time. That does not mean it is easy, but it does mean the ceiling is higher.

## Why Now

The honest answer is that the timing finally feels right.

My path has not been linear. I studied biology and environmental science. I worked in supply management. I moved to China, taught English, learned Mandarin, did translation and proofreading work, and later came back to programming through web development.

None of that was wasted time.

It gave me patience, adaptability, communication skills, and a better understanding of how people learn and solve problems. Those things matter in software too, just in a different form.

Now I have enough life experience to know what kind of work I want. I want to build. I want to create things that are useful, polished, and real. I want a career where learning is not a temporary phase, but part of the job itself.

That is why tech makes sense now.

## What I Am Learning

Right now, I am focused on becoming stronger across both product development and fundamentals.

On the web side, I am continuing to improve with React, modern JavaScript, TypeScript, and full-stack application structure. I want to write cleaner interfaces, better components, and applications that feel good to use instead of just technically working.

On the mobile side, I am building native iOS apps with Swift and SwiftUI. This has become a big part of my direction. I like the discipline of mobile development: the attention to detail, the constraints, the focus on interaction, and the feeling of shipping something that can live directly on someone's phone.

I am also working through CS50 to strengthen my computer science foundations. I do not want to only know frameworks. I want to understand the deeper ideas behind programming: algorithms, memory, data structures, and how computers actually think through problems.

That mix matters to me: practical product building on one side, stronger fundamentals on the other.

## My Roadmap

My roadmap is simple, but serious.

First, I am building a portfolio that honestly shows what I can do. Not just exercises, but projects with a clear purpose, real interfaces, and enough polish to represent my standards.

Then I am using those projects to push myself further. Each one should teach me something: better architecture, better UX, better state management, better backend thinking, better deployment, better App Store release experience.

After that, the goal is real-world apps.

I want to keep shipping products that solve actual problems, especially in areas where calm design and practical usefulness matter. Apps like habit tracking, date planning, language learning, personal organization, and small tools that make daily life a little easier.

That is the direction: portfolio, projects, real products.

Not as a performance. As a path.

## Not a Straight Line

I know my background is different.

But I do not see that as something I need to apologize for. A traditional path can be valuable, but it is not the only way to become good at this.

My route gave me a different kind of preparation. I learned how to adapt, how to keep going when progress is slow, how to communicate across cultures, and how to teach myself difficult things without waiting for perfect conditions.

Those are not replacements for technical skill. Technical skill still has to be earned.

But they are part of the foundation I am building on.

So when I think about my career in tech, I do not think of it as starting late.

I think of it as arriving with context.
  `,
  tags: ["career", "tech", "learning", "iOS", "self-improvement"],
}

export default post
